<template>
    <InputNumber
        v-bind="primeVueAttrs"
        :model-value="modelValue"
        show-buttons
        button-layout="horizontal"
        :pt="primeVuePt"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <template #incrementicon>
            <span class="e-number-input-icon">+</span>
        </template>
        <template #decrementicon>
            <span class="e-number-input-icon">−</span>
        </template>
    </InputNumber>
</template>

<script>
import InputNumber from 'primevue/inputnumber';
/**
 * ENumberinput - Abstraction wrapper around PrimeVue's InputNumber
 *
 * Usage:
 *   <e-numberinput v-model="quantity" :min="0" :max="100" />
 *
 *   <e-numberinput
 *     v-model="price"
 *     :step="0.01"
 *     :min="0"
 *     controls-position="compact"
 *   />
 *
 *   <e-numberinput
 *     v-model="count"
 *     size="is-large"
 *     :controls-rounded="true"
 *   />
 *
 * Props (work in both modes):
 *   - v-model: Number - The numeric value
 *   - min: Number - Minimum value
 *   - max: Number - Maximum value
 *   - step: Number - Increment/decrement step
 *   - controls-position: String - Button position (Buefy: null|"compact", maps to PrimeVue showButtons)
 *   - controls-rounded: Boolean - Rounded buttons (Buefy only)
 *   - size: String - Size classes (Buefy only: "is-small", "is-medium", "is-large")
 *
 * All Buefy b-numberinput props, events, and slots are supported in Buefy mode.
 * PrimeVue InputNumber props are supported in PrimeVue mode.
 */
export default {
    name: 'ENumberinput',
    components: {
        InputNumber,
    },
    inheritAttrs: false,
    props: {
        modelValue: {
            type: Number,
            default: undefined,
        },
        controlsPosition: {
            type: String,
            default: undefined,
        },
        controlsRounded: {
            type: Boolean,
            default: undefined,
        },
        size: {
            type: String,
            default: undefined,
        },
    },
    emits: ['update:modelValue'],
    computed: {
        primeVueAttrs() {
            const attrs = { ...this.$attrs };

            // Remove Buefy-specific props
            delete attrs['controls-position'];
            delete attrs['controls-rounded'];
            delete attrs.size;
            delete attrs.forcePrimeVue;

            return attrs;
        },
        primeVuePt() {
            return {
                incrementButton: {
                    root: {
                        'aria-label': 'Increment',
                        class: 'e-number-input-button',
                    },
                },
                decrementButton: {
                    root: {
                        'aria-label': 'Decrement',
                        class: 'e-number-input-button',
                    },
                },
                buttonGroup: {
                    class: 'e-number-input-button-group',
                },
            };
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

/* Override PrimeVue InputNumber CSS custom properties with SCSS variables */
.p-inputnumber {
    --p-inputnumber-button-background: #{$primary};
    --p-inputnumber-button-color: #{$white};
    --p-inputnumber-button-border-color: #{$primary};
    --p-inputnumber-button-hover-background: #{$primary};
    --p-inputnumber-button-hover-color: #{$white};
    --p-inputnumber-button-hover-border-color: #{$primary};
    --p-inputnumber-button-active-background: #{$primary};
    --p-inputnumber-button-active-color: #{$white};
    --p-inputnumber-button-active-border-color: #{$primary};
}

/* Ensure icon color is white and increase size */
.e-number-input-icon {
    color: $white;
    font-size: 1.5rem;
}

/* Center the input text */
.p-inputnumber input {
    text-align: center;
}
</style>
